import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { cn } from "@/lib/cn";

type Tab = "check" | "spf" | "dkim" | "dmarc";

export function ToolsPage() {
  const [activeTab, setActiveTab] = useState<Tab>("check");

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="page-header page-header-amber">
        <div className="absolute inset-0 bg-grid-white/10" />
        <div className="relative">
          <div className="page-header-content">
            <div className="page-header-icon">
              <svg className="icon-lg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
              </svg>
            </div>
            <div>
              <h1 className="text-2xl font-bold">DNS Tools</h1>
              <p className="text-white/80">Validate and generate SPF, DKIM, and DMARC records</p>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="tabs">
        {[
          { id: "check", label: "Full Check", icon: (
            <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
            </svg>
          )},
          { id: "spf", label: "SPF", icon: (
            <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          )},
          { id: "dkim", label: "DKIM", icon: (
            <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
            </svg>
          )},
          { id: "dmarc", label: "DMARC", icon: (
            <svg className="icon-sm" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          )},
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id as Tab)}
            className={cn(
              "tab",
              activeTab === tab.id ? "tab-active-amber" : "tab-inactive"
            )}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      {activeTab === "check" && <FullCheckTab />}
      {activeTab === "spf" && <SPFTab />}
      {activeTab === "dkim" && <DKIMTab />}
      {activeTab === "dmarc" && <DMARCTab />}
    </div>
  );
}

function FullCheckTab() {
  const [domain, setDomain] = useState("");
  const [result, setResult] = useState<any>(null);

  const mutation = useMutation({
    mutationFn: (d: string) => api.tools.checkAll(d),
    onSuccess: (data) => setResult(data),
  });

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="card-header">
          <div className="card-icon card-icon-amber">
            <svg className="icon-md text-amber" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 01-1.043 3.296 3.745 3.745 0 01-3.296 1.043A3.745 3.745 0 0112 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 01-3.296-1.043 3.745 3.745 0 01-1.043-3.296A3.745 3.745 0 013 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 011.043-3.296 3.746 3.746 0 013.296-1.043A3.746 3.746 0 0112 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 013.296 1.043 3.746 3.746 0 011.043 3.296A3.745 3.745 0 0121 12z" />
            </svg>
          </div>
          <div>
            <h2 className="text-lg font-semibold">Full Domain Check</h2>
            <p className="text-sm text-muted-foreground">
              Check SPF, DKIM, and DMARC configuration for any domain
            </p>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault();
            mutation.mutate(domain);
          }}
          className="flex gap-3"
        >
          <label htmlFor="full-check-domain" className="sr-only">Domain to check</label>
          <input
            id="full-check-domain"
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="example.com"
            className="input input-amber flex-1"
          />
          <button
            type="submit"
            disabled={mutation.isPending || !domain}
            className="btn btn-amber btn-lg"
          >
            {mutation.isPending ? "Checking..." : "Check Domain"}
          </button>
        </form>
      </div>

      {result && (
        <div className="space-y-4">
          {/* Score */}
          <div className="card">
            <div className="flex items-center gap-6">
              <div
                className={cn(
                  "flex h-20 w-20 items-center justify-center rounded-2xl text-3xl font-bold text-white shadow-lg",
                  result.score >= 80
                    ? "bg-gradient-to-br from-green-500 to-emerald-600 shadow-green-500/25"
                    : result.score >= 50
                    ? "bg-gradient-to-br from-yellow-500 to-orange-500 shadow-yellow-500/25"
                    : "bg-gradient-to-br from-red-500 to-rose-600 shadow-red-500/25"
                )}
              >
                {result.score}
              </div>
              <div>
                <div className="text-xl font-semibold">{result.domain}</div>
                <div className="text-sm text-muted-foreground">
                  Email Authentication Score
                </div>
                <div className="mt-2 text-sm">
                  {result.score >= 80 ? (
                    <span className="text-green-600">Excellent configuration</span>
                  ) : result.score >= 50 ? (
                    <span className="text-yellow-600">Needs improvement</span>
                  ) : (
                    <span className="text-red-600">Critical issues found</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* SPF Result */}
          <ResultCard
            title="SPF"
            valid={result.spf.valid}
            record={result.spf.record}
            errors={result.spf.errors}
            warnings={result.spf.warnings}
          />

          {/* DKIM Result */}
          <div className="card">
            <div className="flex items-center gap-3 mb-4">
              <StatusBadge valid={result.dkim.selectorsFound > 0} />
              <h3 className="font-semibold">DKIM</h3>
            </div>
            {result.dkim.selectorsFound > 0 ? (
              <div className="space-y-3">
                <p className="text-sm text-muted-foreground">
                  Found {result.dkim.selectorsFound} DKIM selector(s)
                </p>
                <div className="space-y-2">
                  {result.dkim.selectors.map((s: any, i: number) => (
                    <div key={i} className="flex items-center gap-3 rounded-lg bg-muted/50 p-3">
                      <div className="card-icon-sm card-icon-success">
                        <svg className="icon-sm text-success" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                        </svg>
                      </div>
                      <div>
                        <span className="font-medium">{s.selector}</span>
                        {s.keyType && (
                          <span className="ml-2 text-sm text-muted-foreground">
                            ({s.keyType})
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <p className="text-sm text-red-600">
                No DKIM selectors found
              </p>
            )}
          </div>

          {/* DMARC Result */}
          <ResultCard
            title="DMARC"
            valid={result.dmarc.valid}
            record={result.dmarc.record}
            errors={result.dmarc.errors}
            warnings={result.dmarc.warnings}
            extra={
              result.dmarc.policy && (
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">Policy:</span>
                  <span
                    className={cn(
                      "rounded-full px-2.5 py-0.5 text-xs font-medium",
                      result.dmarc.policy === "reject"
                        ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                        : result.dmarc.policy === "quarantine"
                        ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
                        : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                    )}
                  >
                    {result.dmarc.policy}
                  </span>
                </div>
              )
            }
          />
        </div>
      )}
    </div>
  );
}

function SPFTab() {
  const [domain, setDomain] = useState("");
  const [result, setResult] = useState<any>(null);
  const [showGenerator, setShowGenerator] = useState(false);
  const [generatorOptions, setGeneratorOptions] = useState({
    includeGoogle: false,
    includeMicrosoft: false,
    includeAmazonSES: false,
    includeMailgun: false,
    includeSendgrid: false,
    policy: "softfail" as "fail" | "softfail" | "neutral",
  });
  const [generatedRecord, setGeneratedRecord] = useState("");

  const validateMutation = useMutation({
    mutationFn: (d: string) => api.tools.validateSPF(d),
    onSuccess: (data) => setResult(data),
  });

  const generateMutation = useMutation({
    mutationFn: (opts: any) => api.tools.generateSPF(opts),
    onSuccess: (data: any) => setGeneratedRecord(data.record),
  });

  return (
    <div className="space-y-6">
      {/* Validator */}
      <div className="card">
        <div className="card-header">
          <div className="card-icon card-icon-amber">
            <svg className="icon-md text-amber" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold">SPF Validator</h2>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            validateMutation.mutate(domain);
          }}
          className="flex gap-3"
        >
          <label htmlFor="spf-domain" className="sr-only">Domain to validate SPF</label>
          <input
            id="spf-domain"
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="example.com"
            className="input input-amber flex-1"
          />
          <button
            type="submit"
            disabled={validateMutation.isPending || !domain}
            className="btn btn-amber btn-lg"
          >
            {validateMutation.isPending ? "Checking..." : "Validate"}
          </button>
        </form>

        {result && (
          <div className="mt-6">
            <ResultCard
              title="SPF Record"
              valid={result.valid}
              record={result.record}
              errors={result.errors}
              warnings={result.warnings}
              extra={
                <div className="mt-3 flex items-center gap-2 text-sm">
                  <span className="text-muted-foreground">DNS Lookups:</span>
                  <span className={cn(
                    "rounded-full px-2.5 py-0.5 text-xs font-medium",
                    result.lookupCount > 10
                      ? "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                      : result.lookupCount > 7
                      ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
                      : "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                  )}>
                    {result.lookupCount}/10
                  </span>
                </div>
              }
            />
          </div>
        )}
      </div>

      {/* Generator */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20">
              <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold">SPF Generator</h2>
          </div>
          <button
            onClick={() => setShowGenerator(!showGenerator)}
            className="rounded-lg px-4 py-2 text-sm font-medium text-amber-600 transition-colors hover:bg-amber-50 dark:hover:bg-amber-950"
          >
            {showGenerator ? "Hide" : "Show"}
          </button>
        </div>

        {showGenerator && (
          <div className="space-y-4 pt-2">
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {[
                { key: "includeGoogle", label: "Google Workspace" },
                { key: "includeMicrosoft", label: "Microsoft 365" },
                { key: "includeAmazonSES", label: "Amazon SES" },
                { key: "includeMailgun", label: "Mailgun" },
                { key: "includeSendgrid", label: "SendGrid" },
              ].map(({ key, label }) => (
                <label key={key} className="flex items-center gap-3 rounded-lg bg-muted/50 p-3 cursor-pointer hover:bg-muted transition-colors">
                  <input
                    type="checkbox"
                    checked={(generatorOptions as any)[key]}
                    onChange={(e) =>
                      setGeneratorOptions({
                        ...generatorOptions,
                        [key]: e.target.checked,
                      })
                    }
                    className="h-4 w-4 rounded border-gray-300 text-amber-600 focus:ring-indigo-500"
                  />
                  <span className="text-sm font-medium">{label}</span>
                </label>
              ))}
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">Policy</label>
              <select
                value={generatorOptions.policy}
                onChange={(e) =>
                  setGeneratorOptions({
                    ...generatorOptions,
                    policy: e.target.value as any,
                  })
                }
                className="input input-amber"
              >
                <option value="softfail">Soft Fail (~all) - Recommended</option>
                <option value="fail">Hard Fail (-all) - Strict</option>
                <option value="neutral">Neutral (?all) - Permissive</option>
              </select>
            </div>

            <button
              onClick={() => generateMutation.mutate(generatorOptions)}
              className="btn btn-amber btn-lg"
            >
              Generate
            </button>

            {generatedRecord && (
              <div className="code-block">
                <div className="text-xs font-medium text-muted-foreground mb-2">
                  TXT Record:
                </div>
                <code className="code-text">{generatedRecord}</code>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function DKIMTab() {
  const [domain, setDomain] = useState("");
  const [selector, setSelector] = useState("");
  const [result, setResult] = useState<any>(null);
  const [discovered, setDiscovered] = useState<any[]>([]);

  const validateMutation = useMutation({
    mutationFn: ({ d, s }: { d: string; s: string }) =>
      api.tools.validateDKIM(d, s),
    onSuccess: (data) => setResult(data),
  });

  const discoverMutation = useMutation({
    mutationFn: (d: string) => api.tools.discoverDKIM(d),
    onSuccess: (data: any) => setDiscovered(data.selectors || []),
  });

  return (
    <div className="space-y-6">
      <div className="card">
        <div className="card-header">
          <div className="card-icon card-icon-amber">
            <svg className="icon-md text-amber" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold">DKIM Validator</h2>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            validateMutation.mutate({ d: domain, s: selector });
          }}
          className="space-y-4"
        >
          <div className="flex gap-3">
            <div className="flex-1">
              <label htmlFor="dkim-domain" className="sr-only">Domain</label>
              <input
                id="dkim-domain"
                type="text"
                value={domain}
                onChange={(e) => setDomain(e.target.value)}
                placeholder="example.com"
                className="input input-amber w-full"
              />
            </div>
            <div className="flex-1">
              <label htmlFor="dkim-selector" className="sr-only">DKIM Selector</label>
              <input
                id="dkim-selector"
                type="text"
                value={selector}
                onChange={(e) => setSelector(e.target.value)}
                placeholder="selector (e.g., google, selector1)"
                className="input input-amber w-full"
              />
            </div>
          </div>
          <div className="flex gap-3">
            <button
              type="submit"
              disabled={validateMutation.isPending || !domain || !selector}
              className="btn btn-amber btn-lg"
            >
              {validateMutation.isPending ? "Checking..." : "Validate"}
            </button>
            <button
              type="button"
              onClick={() => discoverMutation.mutate(domain)}
              disabled={discoverMutation.isPending || !domain}
              className="rounded-xl border px-6 py-3 text-sm font-medium transition-colors hover:bg-muted disabled:opacity-50"
            >
              {discoverMutation.isPending ? "Discovering..." : "Discover Selectors"}
            </button>
          </div>
        </form>

        {result && (
          <div className="mt-6">
            <ResultCard
              title={`DKIM: ${result.selector}._domainkey.${result.domain}`}
              valid={result.valid}
              record={result.record}
              errors={result.errors}
              warnings={result.warnings}
              extra={
                result.keyType && (
                  <div className="mt-3 text-sm text-muted-foreground">
                    Key Type: <span className="font-medium text-foreground">{result.keyType}</span>
                  </div>
                )
              }
            />
          </div>
        )}

        {discovered.length > 0 && (
          <div className="mt-6">
            <h3 className="font-medium mb-3">Discovered Selectors</h3>
            <div className="space-y-2">
              {discovered.map((s, i) => (
                <div key={i} className="flex items-center gap-3 rounded-xl bg-muted/50 p-4">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-green-100 dark:bg-green-900/30">
                    <svg className="h-4 w-4 text-green-600" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                    </svg>
                  </div>
                  <div>
                    <span className="font-medium">{s.selector}</span>
                    <span className="ml-2 text-sm text-muted-foreground">{s.keyType} key</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function DMARCTab() {
  const [domain, setDomain] = useState("");
  const [result, setResult] = useState<any>(null);
  const [showGenerator, setShowGenerator] = useState(false);
  const [generatorOptions, setGeneratorOptions] = useState({
    policy: "none" as "none" | "quarantine" | "reject",
    ruaEmail: "",
    rufEmail: "",
  });
  const [generatedRecord, setGeneratedRecord] = useState("");

  const validateMutation = useMutation({
    mutationFn: (d: string) => api.tools.validateDMARC(d),
    onSuccess: (data) => setResult(data),
  });

  const generateMutation = useMutation({
    mutationFn: (opts: any) => api.tools.generateDMARC(opts),
    onSuccess: (data: any) => setGeneratedRecord(data.record),
  });

  return (
    <div className="space-y-6">
      {/* Validator */}
      <div className="card">
        <div className="card-header">
          <div className="card-icon card-icon-amber">
            <svg className="icon-md text-amber" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
            </svg>
          </div>
          <h2 className="text-lg font-semibold">DMARC Validator</h2>
        </div>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            validateMutation.mutate(domain);
          }}
          className="flex gap-3"
        >
          <label htmlFor="dmarc-domain" className="sr-only">Domain to validate DMARC</label>
          <input
            id="dmarc-domain"
            type="text"
            value={domain}
            onChange={(e) => setDomain(e.target.value)}
            placeholder="example.com"
            className="input input-amber flex-1"
          />
          <button
            type="submit"
            disabled={validateMutation.isPending || !domain}
            className="btn btn-amber btn-lg"
          >
            {validateMutation.isPending ? "Checking..." : "Validate"}
          </button>
        </form>

        {result && (
          <div className="mt-6">
            <ResultCard
              title="DMARC Record"
              valid={result.valid}
              record={result.record}
              errors={result.errors}
              warnings={result.warnings}
              extra={
                result.policy && (
                  <div className="mt-3 space-y-2 text-sm">
                    <div className="flex items-center gap-2">
                      <span className="text-muted-foreground">Policy:</span>
                      <span
                        className={cn(
                          "rounded-full px-2.5 py-0.5 text-xs font-medium",
                          result.policy === "reject"
                            ? "bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400"
                            : result.policy === "quarantine"
                            ? "bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400"
                            : "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400"
                        )}
                      >
                        {result.policy}
                      </span>
                    </div>
                    {result.ruaAddresses.length > 0 && (
                      <div>
                        <span className="text-muted-foreground">Reports to: </span>
                        <span className="font-medium">{result.ruaAddresses.join(", ")}</span>
                      </div>
                    )}
                  </div>
                )
              }
            />
          </div>
        )}
      </div>

      {/* Generator */}
      <div className="card">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-amber-500/20 to-orange-500/20">
              <svg className="h-5 w-5 text-amber-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.594 3.94c.09-.542.56-.94 1.11-.94h2.593c.55 0 1.02.398 1.11.94l.213 1.281c.063.374.313.686.645.87.074.04.147.083.22.127.324.196.72.257 1.075.124l1.217-.456a1.125 1.125 0 011.37.49l1.296 2.247a1.125 1.125 0 01-.26 1.431l-1.003.827c-.293.24-.438.613-.431.992a6.759 6.759 0 010 .255c-.007.378.138.75.43.99l1.005.828c.424.35.534.954.26 1.43l-1.298 2.247a1.125 1.125 0 01-1.369.491l-1.217-.456c-.355-.133-.75-.072-1.076.124a6.57 6.57 0 01-.22.128c-.331.183-.581.495-.644.869l-.213 1.28c-.09.543-.56.941-1.11.941h-2.594c-.55 0-1.02-.398-1.11-.94l-.213-1.281c-.062-.374-.312-.686-.644-.87a6.52 6.52 0 01-.22-.127c-.325-.196-.72-.257-1.076-.124l-1.217.456a1.125 1.125 0 01-1.369-.49l-1.297-2.247a1.125 1.125 0 01.26-1.431l1.004-.827c.292-.24.437-.613.43-.992a6.932 6.932 0 010-.255c.007-.378-.138-.75-.43-.99l-1.004-.828a1.125 1.125 0 01-.26-1.43l1.297-2.247a1.125 1.125 0 011.37-.491l1.216.456c.356.133.751.072 1.076-.124.072-.044.146-.087.22-.128.332-.183.582-.495.644-.869l.214-1.281z" />
                <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </div>
            <h2 className="text-lg font-semibold">DMARC Generator</h2>
          </div>
          <button
            onClick={() => setShowGenerator(!showGenerator)}
            className="rounded-lg px-4 py-2 text-sm font-medium text-amber-600 transition-colors hover:bg-amber-50 dark:hover:bg-amber-950"
          >
            {showGenerator ? "Hide" : "Show"}
          </button>
        </div>

        {showGenerator && (
          <div className="space-y-4 pt-2">
            <div>
              <label className="block text-sm font-medium mb-2">Policy</label>
              <select
                value={generatorOptions.policy}
                onChange={(e) =>
                  setGeneratorOptions({
                    ...generatorOptions,
                    policy: e.target.value as any,
                  })
                }
                className="w-full rounded-xl border bg-background px-4 py-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
              >
                <option value="none">None - Monitor only</option>
                <option value="quarantine">Quarantine - Mark as spam</option>
                <option value="reject">Reject - Block delivery</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium mb-2">
                Aggregate Report Email (rua)
              </label>
              <input
                type="email"
                value={generatorOptions.ruaEmail}
                onChange={(e) =>
                  setGeneratorOptions({
                    ...generatorOptions,
                    ruaEmail: e.target.value,
                  })
                }
                placeholder="dmarc-reports@example.com"
                className="w-full rounded-xl border bg-background px-4 py-3 text-sm focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/20"
              />
            </div>

            <button
              onClick={() => generateMutation.mutate(generatorOptions)}
              disabled={!generatorOptions.ruaEmail}
              className="btn btn-amber btn-lg"
            >
              Generate
            </button>

            {generatedRecord && (
              <div className="code-block">
                <div className="text-xs font-medium text-muted-foreground mb-2">
                  TXT Record for _dmarc.yourdomain.com:
                </div>
                <code className="code-text">{generatedRecord}</code>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

function StatusBadge({ valid }: { valid: boolean }) {
  return (
    <div className={cn("status-badge", valid ? "status-badge-success" : "status-badge-error")}>
      {valid ? (
        <svg className="icon-md text-success" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
        </svg>
      ) : (
        <svg className="icon-md text-error" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
        </svg>
      )}
    </div>
  );
}

function ResultCard({
  title,
  valid,
  record,
  errors,
  warnings,
  extra,
}: {
  title: string;
  valid: boolean;
  record: string | null;
  errors: string[];
  warnings: string[];
  extra?: React.ReactNode;
}) {
  return (
    <div className="rounded-xl border bg-card p-5">
      <div className="flex items-center gap-3 mb-4">
        <StatusBadge valid={valid} />
        <h3 className="font-semibold">{title}</h3>
      </div>

      {record && (
        <div className="code-block">
          <code className="code-text">{record}</code>
        </div>
      )}

      {errors.length > 0 && (
        <div className="mt-4 space-y-2">
          {errors.map((e, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-red-600">
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
              </svg>
              {e}
            </div>
          ))}
        </div>
      )}

      {warnings.length > 0 && (
        <div className="mt-4 space-y-2">
          {warnings.map((w, i) => (
            <div key={i} className="flex items-center gap-2 text-sm text-yellow-600">
              <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
              </svg>
              {w}
            </div>
          ))}
        </div>
      )}

      {extra}
    </div>
  );
}
